import React from 'react'
import { motion } from 'framer-motion'
import { FaShieldAlt, FaUserLock, FaCookieBite, FaEnvelope, FaChild, FaGlobe, FaLock } from 'react-icons/fa'
import { BsShieldCheck } from 'react-icons/bs'

const SectionCard = ({ title, children }) => (
  <div className="bg-white rounded-xl p-6 border border-slate-200 mb-6">
    <h2 className="text-lg font-bold text-slate-900 mb-4">{title}</h2>
    <div className="text-slate-600 leading-relaxed">{children}</div>
  </div>
)

export const Privacy = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="bg-linear-to-br from-slate-900 via-slate-800 to-blue-900 py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 bg-blue-500/20 border border-blue-400/30 rounded-full px-4 py-2 text-blue-300 text-sm font-medium mb-6">
              <FaShieldAlt />
              Your Privacy Matters
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-4">
              Privacy Policy
            </h1>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              www.stringssg.com Privacy Policy
            </p>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Intro */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8">
          <p className="text-slate-700 leading-relaxed">
            This privacy policy has been compiled to better serve those who are concerned with how their &apos;Personally Identifiable Information&apos; (PII) is being used online. 
            PII, as described in US privacy law and information security, is information that can be used on its own or with other information to identify, contact, or locate 
            a single person, or to identify an individual in context. Please read our privacy policy carefully to get a clear understanding of how we collect, use, 
            protect or otherwise handle your Personally Identifiable Information in accordance with our website.
          </p>
        </div>

        <SectionCard title="What personal information do we collect from the people that visit our blog, website or app?">
          <p>
            When ordering or registering on our site, as appropriate, you may be asked to enter your name, email address, phone number or other details to help you 
            with your experience.
          </p>
        </SectionCard>

        <SectionCard title="When do we collect information?">
          <p>
            We collect information from you when you register on our site, place an order, subscribe to a newsletter, respond to a survey, fill out a form, Use Live Chat, 
            Open a Support Ticket or enter information on our site.
          </p>
          <p className="mt-3">
            Provide us with feedback on our products or services.
          </p>
        </SectionCard>

        <SectionCard title="How do we use your information?">
          <p className="mb-4">We may use the information we collect from you when you register, make a purchase, sign up for our newsletter, respond to a survey or marketing 
          communication, surf the website, or use certain other site features in the following ways:</p>
          <ul className="space-y-2">
            <li className="flex gap-2"><span className="text-blue-500">•</span><span>To help you to connect with relevant service providers when you open a service request.</span></li>
            <li className="flex gap-2"><span className="text-blue-500">•</span><span>If you are a service provider, your contact information is available to public who are looking for your service</span></li>
            <li className="flex gap-2"><span className="text-blue-500">•</span><span>To personalize your experience and to allow us to deliver the type of content and product offerings in which you are most interested.</span></li>
            <li className="flex gap-2"><span className="text-blue-500">•</span><span>To improve our website in order to better serve you.</span></li>
            <li className="flex gap-2"><span className="text-blue-500">•</span><span>To allow us to better service you in responding to your customer service requests.</span></li>
            <li className="flex gap-2"><span className="text-blue-500">•</span><span>To administer a contest, promotion, survey or other site feature.</span></li>
            <li className="flex gap-2"><span className="text-blue-500">•</span><span>To quickly process your transactions.</span></li>
            <li className="flex gap-2"><span className="text-blue-500">•</span><span>To ask for ratings and reviews of services or products</span></li>
            <li className="flex gap-2"><span className="text-blue-500">•</span><span>To follow up with them after correspondence (live chat, email or phone inquiries)</span></li>
          </ul>
        </SectionCard>

        <SectionCard title="How do we protect your information?">
          <div className="space-y-4">
            <p>
              We do not use vulnerability scanning and/or scanning to PCI standards.
              An external PCI compliant payment gateway handles all CC transactions.
              We do not use Malware Scanning.
            </p>
            <p>
              Your personal information is contained behind secured networks and is only accessible by a limited number of persons who have special access rights to such systems, 
              and are required to keep the information confidential. In addition, all sensitive/credit information you supply is encrypted via Secure Socket Layer (SSL) technology.
            </p>
            <p>
              We implement a variety of security measures when a user enters, submits, or accesses their information to maintain the safety of your personal information.
            </p>
            <p>
              All transactions are processed through a gateway provider and are not stored or processed on our servers.
            </p>
          </div>
        </SectionCard>

        <SectionCard title="Privacy Rights and Choices">
          <p className="mb-4">
            Please reach out to us at <a className="text-blue-600 hover:text-blue-700 font-medium" href="mailto:support@stringssg.com">support@stringssg.com</a> for the following requests.
          </p>
          <ul className="space-y-2">
            <li className="flex gap-2"><span className="text-blue-500">•</span><span>Information on what personal we have about you.</span></li>
            <li className="flex gap-2"><span className="text-blue-500">•</span><span>Deletion of such personal data or direct you to applicable tools.</span></li>
          </ul>
        </SectionCard>

        {/* Cookie Section with Icon - id is the target of the "/" cookie notice */}
        <div id="cookies" className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-200 mt-12 scroll-mt-20">
          <div className="w-12 h-12 bg-linear-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg">
            <FaCookieBite className="text-xl text-white" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900">Cookies</h2>
        </div>

        <SectionCard title="Do we use 'cookies'?">
          <p>
            Yes. Cookies are small files that a site or its service provider transfers to your computer&apos;s hard drive through your Web browser (if you allow) that 
            enables the site&apos;s or service provider&apos;s systems to recognize your browser and capture and remember certain information. For instance, we use cookies 
            to help us remember and process the items in your shopping cart. They are also used to help us understand your preferences based on previous or current 
            site activity, which enables us to provide you with improved services. We also use cookies to help us compile aggregate data about site traffic and 
            site interaction so that we can offer better site experiences and tools in the future.
          </p>
        </SectionCard>

        <div className="bg-slate-50 rounded-xl p-6 border border-slate-200 mb-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4">We use cookies to:</h3>
          <ul className="space-y-2 text-slate-600">
            <li className="flex gap-2"><span className="text-amber-500">•</span><span>Help remember and process the items in the shopping cart.</span></li>
            <li className="flex gap-2"><span className="text-amber-500">•</span><span>Understand and save user&apos;s preferences for future visits.</span></li>
            <li className="flex gap-2"><span className="text-amber-500">•</span><span>Keep track of advertisements.</span></li>
            <li className="flex gap-2"><span className="text-amber-500">•</span><span>Compile aggregate data about site traffic and site interactions in order to offer better site experiences and tools in the future. We may also use trusted third-party services that track this information on our behalf.</span></li>
          </ul>
          <p className="text-slate-600 mt-4">
            You can choose to have your computer warn you each time a cookie is being sent, or you can choose to turn off all cookies. 
            You do this through your browser settings. Since browser is a little different, look at your browser&apos;s Help Menu to learn the correct way to modify your cookies.
          </p>
        </div>

        <SectionCard title="If users disable cookies in their browser:">
          <p>
            If you turn cookies off, Some of the features that make your site experience more efficient may not function properly.Some of the features that make your 
            site experience more efficient and may not function properly.
          </p>
        </SectionCard>

        {/* Third Party Section */}
        <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-200 mt-12">
          <div className="w-12 h-12 bg-linear-to-br from-slate-600 to-slate-800 rounded-xl flex items-center justify-center shadow-lg">
            <FaGlobe className="text-xl text-white" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900">Third-Party Information</h2>
        </div>

        <SectionCard title="Third-party disclosure">
          <p>
            We do not sell, trade, or otherwise transfer to outside parties your Personally Identifiable Information unless we provide users with advance notice. 
            This does not include website hosting partners and other parties who assist us in operating our website, conducting our business, or serving our users, 
            so long as those parties agree to keep this information confidential. We may also release information when it&apos;s release is appropriate to comply with the law, 
            enforce our site policies, or protect ours or others&apos; rights, property or safety.
          </p>
        </SectionCard>

        <SectionCard title="Third-party links">
          <p>
            Occasionally, at our discretion, we may include or offer third-party products or services on our website. 
            These third-party sites have separate and independent privacy policies. We therefore have no responsibility or liability for the content and 
            activities of these linked sites. Nonetheless, we seek to protect the integrity of our site and welcome any feedback about these sites.
          </p>
        </SectionCard>

        <SectionCard title="Google">
          <p>
            Google&apos;s advertising requirements can be summed up by Google&apos;s Advertising Principles. They are put in place to provide a positive experience for users. https://support.google.com/adwordspolicy/answer/1316548?hl=en
          </p>
          <p className="mt-3">
            We have not enabled Google AdSense on our site but we may do so in the future.
          </p>
        </SectionCard>

        {/* Compliance Section */}
        <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-200 mt-12">
          <div className="w-12 h-12 bg-linear-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg">
            <BsShieldCheck className="text-xl text-white" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900">Compliance</h2>
        </div>

        <SectionCard title="COPPA (Children Online Privacy Protection Act)">
          <p>
            When it comes to the collection of personal information from children under the age of 13 years old, the Children&apos;s Online Privacy Protection Act (COPPA) puts 
            parents in control. The Federal Trade Commission, United States&apos; consumer protection agency, enforces the COPPA Rule, which spells out what operators of websites 
            and online services must do to protect childrens privacy and safety online.
          </p>
          <p className="mt-3">
            We do not specifically market to children under the age of 13 years old.
            Do we let third-parties, including ad networks or plug-ins collect PII from children under 13?
          </p>
        </SectionCard>

        <div className="bg-white rounded-xl p-6 border border-slate-200 mb-6">
          <h2 className="text-lg font-bold text-slate-900 mb-4">CAN SPAM Act</h2>
          <p className="text-slate-600 leading-relaxed mb-6">
            The CAN-SPAM Act is a law that sets the rules for commercial email, establishes requirements for commercial messages, gives recipients the right to have emails 
            stopped from being sent to them, and spells out tough penalties for violations.
          </p>

          <div className="bg-slate-50 rounded-lg p-4 mb-4">
            <h3 className="font-bold text-slate-900 mb-3">We collect your email address in order to:</h3>
            <ul className="space-y-2 text-slate-600">
              <li className="flex gap-2"><span className="text-green-500">•</span><span>Send information, respond to inquiries, and/or other requests or questions.</span></li>
              <li className="flex gap-2"><span className="text-green-500">•</span><span>Process orders and to send information and updates pertaining to orders.</span></li>
              <li className="flex gap-2"><span className="text-green-500">•</span><span>Send you additional information related to your product and/or service.</span></li>
              <li className="flex gap-2"><span className="text-green-500">•</span><span>Market to our mailing list or continue to send emails to our clients after the original transaction has occurred.</span></li>
            </ul>
          </div>

          <div className="bg-slate-50 rounded-lg p-4 mb-4">
            <h3 className="font-bold text-slate-900 mb-3">To be in accordance with CANSPAM, we agree to the following:</h3>
            <ul className="space-y-2 text-slate-600">
              <li className="flex gap-2"><span className="text-green-500">•</span><span>Not use false or misleading subjects or email addresses.</span></li>
              <li className="flex gap-2"><span className="text-green-500">•</span><span>Identify the message as an advertisement in some reasonable way.</span></li>
              <li className="flex gap-2"><span className="text-green-500">•</span><span>Include the physical address of our business or site headquarters.</span></li>
              <li className="flex gap-2"><span className="text-green-500">•</span><span>Monitor third-party email marketing services for compliance, if one is used.</span></li>
              <li className="flex gap-2"><span className="text-green-500">•</span><span>Honor opt-out/unsubscribe requests quickly.</span></li>
              <li className="flex gap-2"><span className="text-green-500">•</span><span>Allow users to unsubscribe by using the link at the bottom of each email.</span></li>
            </ul>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-bold text-slate-900 mb-2">If at any time you would like to unsubscribe from receiving future emails, you can email us at:</h3>
            <p className="text-slate-600">
              <a href="mailto:support@stringssg.com" className="text-blue-600 hover:text-blue-700 font-medium">support@stringssg.com</a> and we will promptly remove you from <strong>ALL</strong> correspondence.
            </p>
          </div>
        </div>

        {/* Contact Section */}
        <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-200 mt-12">
          <div className="w-12 h-12 bg-linear-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
            <FaEnvelope className="text-xl text-white" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900">Contact Us</h2>
        </div>

        <div className="bg-linear-to-br from-slate-50 to-blue-50 rounded-xl p-6 border border-slate-200">
          <h3 className="text-lg font-bold text-slate-900 mb-4">Contacting Us</h3>
          <p className="text-slate-600 mb-4">
            If there are any questions regarding this privacy policy, you may contact us using the information below.
          </p>
          <div className="space-y-2 text-slate-700">
            <p className="font-medium">www.stringssg.com</p>
            <p>Singapore</p>
            <p><a href="mailto:support@stringssg.com" className="text-blue-600 hover:text-blue-700">support@stringssg.com</a></p>
          </div>
        </div>

        {/* Last Edited */}
        <div className="text-center mt-12 pt-8 border-t border-slate-200">
          <p className="text-slate-500 text-sm">
            Last Edited on 2025-08-17
          </p>
        </div>
      </div>
    </div>
  )
}
